import React, { useState, useCallback } from 'react';
import { useAppSelector, useSnackbar, useAppDispatch } from 'src/utils/Hooks';
import { create } from 'zustand';
import { GenericModal } from 'src/components/Modals/GenericModal';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { PortfolioColumnDefinition, PortfolioIdDefinition, PortfolioBudgetDefinition } from './columnDefinitions';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import colors from 'src/utils/colors';
import { updateCampaignPortfolio } from './utils';
import { useExceededBudgetModal } from './ExceededBudgetModal';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { fetchMainEntity } from 'src/store/modules/entityService/operations';

const modalSx = {
  '& .MuiPaper-root': {
    width: '752px',
    borderRadius: '8px',
    padding: '22px 48px 20px 48px',
    overflow: 'hidden'
  },
  '& .editPortfolio__link': {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2
  },
  '& .genericModal__header > svg': {
    height: '24px !important',
    width: '24px !important',
    marginRight: '-24.5px'
  },
  '& .appTableContainer': {
    maxHeight: '347px !important',
    overflowY: 'auto'
  },
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 2
  },
  '.genericModal__header': {
    position: 'relative',
    flexDirection: 'column',
    paddingBottom: '22px'
  },
  '& .genericModal__header p': {
    position: 'absolute',
    right: '50%',
    transform: 'translateX(50%)'
  },
  '& .genericModal__header::after': {
    content: '""',
    display: 'block',
    height: '1px',
    backgroundColor: '#E5E5E5',
    position: 'absolute',
    width: '1000px !important',
    bottom: 0
  },
  '& .MuiInputBase-root': {
    width: '253px !important'
  },
  'td:first-child': {
    paddingLeft: '0 !important',
    width: '312px',
    boxSizing: 'border-box',
    paddingRight: '24px'
  },
  'th:first-child': {
    paddingLeft: '0 !important'
  },
  th: {
    padding: '0px 24px 16px 24px !important'
  },
  tr: {
    height: 'fit-content !important'
  },
  'td.MuiTableCell-alignLeft': {
    border: 'none !important',
    maxWidth: '312px',
    padding: '0px 24px',
    marginTop: '8px',
    height: '48px !important',
    display: 'table-cell'
  },
  td: {
    height: '0px !important',
    display: 'block'
  },
  '& .MuiTableCell-head': {
    borderBottom: `1px ${colors.graysSecondary} solid !important`,
    marginBottom: '8px'
  }
};

export const PortfolioEditStore = create((set) => ({
  selectedPortfolio: '',
  setSelectedPortfolio: (portfolioId: string) => set({ selectedPortfolio: portfolioId })
}));

export const PortfolioEditModal = ({ open, setOpen }) => {
  const { adPortfolios, adCampaigns } = useAppSelector((state) => state);
  const { selectedPortfolio, setSelectedPortfolio } = PortfolioEditStore();
  const { showSnackbar } = useSnackbar();
  const [searchText, setSearchText] = useState('');
  const entity = useAppSelector((state) => state.entityService.mainEntity);
  const dispatch = useAppDispatch();
  const { setOpen: setExceededBudgetOpen, setEntity, setBudgets } = useExceededBudgetModal();

  const filteredPortfolios = adPortfolios
    .filter(
      (portfolio) =>
        portfolio.name.toLowerCase().includes(searchText.toLowerCase()) &&
        entity?.extendedAttributes?.portfolioId !== portfolio.id
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const onClose = useCallback(() => {
    setOpen(false);
    setSelectedPortfolio('');
  }, [setOpen, setSelectedPortfolio]);

  const handleSubmit = useCallback(async () => {
    try {
      const selectedPortfolioObj = adPortfolios.find((portfolio) => portfolio.id === selectedPortfolio);
      const selectedCampaign = adCampaigns.find((campaign) => campaign.campaignId === entity.id);
      if (selectedPortfolioObj && selectedCampaign) {
        const updatedCampaign = {
          ...selectedCampaign,
          extendedAttributes: {
            ...selectedCampaign.extendedAttributes,
            portfolioId: selectedPortfolioObj.id,
            portfolioIdApi: selectedPortfolioObj.id,
            portfolioName: selectedPortfolioObj.name
          }
        };
        await updateCampaignPortfolio(updatedCampaign, selectedPortfolioObj);
        showSnackbar({
          message: 'Campaign moved successfully',
          type: 'success'
        });
        dispatch(fetchMainEntity('adCampaign', entity.id, entity.extendedAttributes?.retailerId));
      }
    } catch (error) {
      if (error.message !== 'Portfolio budget exceeded') {
        showSnackbar({
          message: 'An error occurred. Please reach out to support@stackline.com for further guidance.',
          type: 'error'
        });
      } else {
        setEntity(entity);
        setBudgets(error.data);
        setExceededBudgetOpen();
      }
    } finally {
      setOpen(false);
    }
  }, [
    adPortfolios,
    adCampaigns,
    selectedPortfolio,
    entity,
    showSnackbar,
    dispatch,
    setEntity,
    setBudgets,
    setExceededBudgetOpen,
    setOpen
  ]);

  return (
    <GenericModal open={open} onClose={onClose} sx={modalSx}>
      <GenericModal.Header title="Move Campaign" onClose={onClose} />
      <GenericModal.Body>
        <Text
          variant="body2"
          sx={{
            fontWeight: 500,
            marginTop: '24px'
          }}
        >
          Portfolios
        </Text>
        <Text
          variant="body2"
          sx={{
            marginTop: '8px',
            marginBottom: '16px'
          }}
        >
          Select the portfolio that you would like to move this campaign into.
        </Text>
        <SearchTextField
          placeholder="Search portfolios"
          value={searchText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
        />
        <div style={{ marginTop: '32px', height: '327px' }}>
          <EntityTable
            rowData={filteredPortfolios}
            columnDefs={[PortfolioColumnDefinition, PortfolioIdDefinition, PortfolioBudgetDefinition]}
            shouldModifyColumnDefs={false}
            initialPageSize={10000}
          />
        </div>
        <Flex
          justifyContent="space-between"
          sx={{
            paddingTop: '20px',
            position: 'relative',
            backgroundColor: 'white',
            '&::after': {
              content: '""',
              display: 'block',
              height: '1px',
              marginLeft: '-50px',
              backgroundColor: '#E5E5E5',
              position: 'absolute',
              width: '1000px !important',
              top: 0
            }
          }}
        >
          <SlButton
            onClick={() => {
              setSelectedPortfolio('');
            }}
            buttonSx={{
              border: `1px solid ${colors.graysSecondary}`,
              backgroundColor: 'white',
              padding: '6px 20px',
              '&:hover': {
                backgroundColor: 'white'
              }
            }}
            textSx={{
              color: colors.darkBlue
            }}
          >
            Clear
          </SlButton>
          <SlButton
            onClick={async () => {
              handleSubmit();
            }}
            disabled={!selectedPortfolio}
            buttonSx={{
              backgroundColor: colors.darkBlue,
              marginLeft: '16px',
              border: `1px solid ${colors.darkBlue}`,
              padding: '6px 20px',
              '&:hover': {
                backgroundColor: colors.darkBlue
              }
            }}
            textSx={{
              color: colors.white
            }}
          >
            Save
          </SlButton>
        </Flex>
      </GenericModal.Body>
    </GenericModal>
  );
};
